import { cx } from '@flowus/common/cx';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import type { FC } from 'react';
import { useMemo } from 'react';
import { Button } from 'src/common/components/button';
import { useBuyGuestCount } from 'src/components/buy-guest-count-modal';
import { useModel } from 'src/common/create-model';
import { useCurrentSpace } from 'src/hooks/space';
import { useCurrentSpaceUsers } from 'src/hooks/space/use-current-space-users';
import { useIsProSpace } from 'src/hooks/space/use-is-pro-space';
import { useGuestsList } from 'src/services/app';
import { useLimitConfig } from 'src/services/app/hook/subscription-data';
import { SettingModalCommon, SettingProvider } from '../common';
import { Option } from '../option';
import { OpenSettingFrom, SettingMenuType } from '../type';

export const SpaceInfoMembers: FC = () => {
  const { t } = useAppTranslation();
  const { setCurrentSettingMenu, setIsFrom } = useModel(SettingProvider);
  const isProSpace = useIsProSpace();
  const users = useCurrentSpaceUsers();
  const currentSpace = useCurrentSpace();
  const spaceUser = Object.values(users);
  const guests = useGuestsList();
  const buyGuestCount = useBuyGuestCount();
  const diver = <span className="mx-4 text-grey3 text-t2">|</span>;
  const { isMaxGuests, membersMaxLimit, memberMaxLimitDesc, guestMaxLimitDesc } = useLimitConfig();

  const isMaxPeople = useMemo(() => {
    return spaceUser.length >= membersMaxLimit;
  }, [membersMaxLimit, spaceUser.length]);

  if (__PRIVATE__) return null;

  return (
    <>
      <Option
        title={t('components.space_info_setting.space_info_members.title')}
        description={
          <SettingModalCommon.Item
            leftChild={
              <div className="flex whitespace-nowrap mr-2">
                <div>
                  {t('common.space_member')}&nbsp;
                  <span className={cx(isMaxPeople && 'text-red')}>
                    {spaceUser.length}/{memberMaxLimitDesc}
                  </span>
                </div>
                {diver}
                <div className="flex">
                  {t('components.space_info_setting.space_info_members.member_groups')}&nbsp;
                  <span>{currentSpace.permissionGroups?.length || 0}</span>
                </div>
                {diver}
                <div>
                  {t('common.external_collaborator')}
                  &nbsp;
                  <span className={cx(isMaxGuests && 'text-red')}>
                    {guests.length}/{guestMaxLimitDesc}
                  </span>
                </div>
              </div>
            }
            rightChild={
              <div className="flex space-x-2">
                {!__BUILD_IN__ && isProSpace && (
                  <Button
                    onClick={() => {
                      buyGuestCount({ spaceId: currentSpace.uuid });
                    }}
                  >
                    {t('common.buy_more_guests')}
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setCurrentSettingMenu(SettingMenuType.upgrade);
                    setIsFrom(OpenSettingFrom.dashboard_member);
                  }}
                >
                  {t('common.buy_more_seats')}
                </Button>
              </div>
            }
          />
        }
      />
    </>
  );
};
